import React, { useEffect, useState } from "react"
import { NavLink } from "react-router-dom"
import { db } from "../../../utils/firebase"
import moment from "moment"

import WindowCard from "../../../components/ui/window-card/window-card"
import ViewArchivedEnquiry from "../../../components/enquiry/view/archived-view"
import { statusLabel } from "./activity/log"

const formatDate = (seconds) => (seconds ? moment(seconds, "X").format("MMM D, YYYY") : "—")

/**
 * Detailed list of the client's prior enquiries and bookings — shown on
 * the Client history tab. The right-rail Client card shows summary counts;
 * this tab shows row-level detail with links.
 */
export default function ClientHistory({ clientID, currentEnquiryID }) {
	const [enquiries, setEnquiries] = useState([])
	const [bookings, setBookings] = useState([])
	const [loading, setLoading] = useState(true)
	const [viewArchivedEnquiry, setViewArchivedEnquiry] = useState(null)

	useEffect(() => {
		if (!clientID) {
			setLoading(false)
			return
		}

		Promise.all([
			db
				.collection("enquiries")
				.where("client", "==", clientID)
				.get()
				.then((snap) =>
					snap.docs
						.filter((d) => d.id !== currentEnquiryID)
						.map((d) => ({ id: d.id, isArchived: false, ...d.data() }))
				),
			db
				.collection("enquiries_removed")
				.where("enquiry.client", "==", clientID)
				.get()
				.then((snap) =>
					snap.docs.map((d) => {
						const data = d.data()
						return {
							id: d.id,
							isArchived: true,
							archived_from_stats: data.archived_from_stats,
							...(data.enquiry || {}),
						}
					})
				),
			db
				.collection("bookings")
				.where("client", "==", clientID)
				.get()
				.then((snap) =>
					snap.docs
						.map((d) => ({ id: d.id, ...d.data() }))
						.sort((a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0))
				),
		]).then(([activeEnquiries, archivedEnquiries, b]) => {
			const allEnquiries = [...activeEnquiries, ...archivedEnquiries].sort(
				(a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0)
			)
			setEnquiries(allEnquiries)
			setBookings(b)
			setLoading(false)
		})
	}, [clientID, currentEnquiryID])

	if (!clientID) {
		return (
			<WindowCard title="Client history">
				<div className="enquiry-card-body">
					<small className="no-value">No client linked.</small>
				</div>
			</WindowCard>
		)
	}

	return (
		<>
			<WindowCard title={`Prior bookings (${bookings.length})`}>
				{loading && (
					<div className="enquiry-card-body">
						<small className="no-value">Loading…</small>
					</div>
				)}
				{!loading && bookings.length === 0 && (
					<div className="enquiry-card-body">
						<small className="no-value">No prior bookings.</small>
					</div>
				)}
				{!loading && bookings.length > 0 && (
					<table className="enquiry-history-table">
						<thead>
							<tr>
								<th>Reference</th>
								<th>Booked</th>
								<th>Travel from</th>
								<th>Location</th>
								<th>Quoted</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{bookings.map((b) => (
								<tr key={b.id}>
									<td>{b.reference || "—"}</td>
									<td>{formatDate(b.created?.seconds)}</td>
									<td>{formatDate(b.date_from?.seconds)}</td>
									<td>{b.enquiry?.location || b.booked_location || "—"}</td>
									<td>£{b.enquiry?.quoted ?? b.total_cost ?? 0}</td>
									<td className="is-button">
										<NavLink to={`/booking/${b.id}`}>View</NavLink>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</WindowCard>

			<WindowCard title={`Prior enquiries (${enquiries.length})`}>
				{loading && (
					<div className="enquiry-card-body">
						<small className="no-value">Loading…</small>
					</div>
				)}
				{!loading && enquiries.length === 0 && (
					<div className="enquiry-card-body">
						<small className="no-value">No prior enquiries.</small>
					</div>
				)}
				{!loading && enquiries.length > 0 && (
					<table className="enquiry-history-table">
						<thead>
							<tr>
								<th>Created</th>
								<th>Travel from</th>
								<th>Location</th>
								<th>Status</th>
								<th>Quoted</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{enquiries.map((e) => (
								<tr key={e.id}>
									<td>{formatDate(e.created?.seconds)}</td>
									<td>{formatDate(e.date_from?.seconds)}</td>
									<td>{e.location || "—"}</td>
									<td>
										{e.isArchived && e.archived_from_stats ? (
											<span className="ui-badge NEGATIVE">Archived from stats</span>
										) : e.isArchived ? (
											<span className="ui-badge">Deleted</span>
										) : e.status ? (
											<span className={`enquiry-stat-status is-${e.status.toLowerCase()}`}>{statusLabel(e.status)}</span>
										) : (
											<small className="no-value">—</small>
										)}
									</td>
									<td>£{e.quoted || 0}</td>
									<td className="is-button">
										{e.isArchived ? (
											<button
												type="button"
												onClick={() => setViewArchivedEnquiry(e)}>
												View
											</button>
										) : (
											<NavLink to={`/enquiry/${e.id}`}>View</NavLink>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</WindowCard>

			{viewArchivedEnquiry && (
				<ViewArchivedEnquiry
					enquiryID={viewArchivedEnquiry.id}
					enquiryData={viewArchivedEnquiry}
					close={() => setViewArchivedEnquiry(null)}
				/>
			)}
		</>
	)
}
